import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ItemCode } from "@/components/Bits";
import { cn } from "@/lib/utils";

/**
 * Pick an item by code or name. Shows the live stock count next to each option
 * so you find out something is empty while choosing it, not after saving.
 */
export default function ItemPicker({ items, value, onChange, placeholder = "Choose an item" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => items.find((i) => i.item_code === value) || null,
    [items, value]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? items.filter(
          (i) =>
            i.item_code.toLowerCase().includes(q) ||
            i.name.toLowerCase().includes(q) ||
            (i.category || "").toLowerCase().includes(q)
        )
      : items;
    return base.slice(0, 60);
  }, [items, query]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 40);
    else setQuery("");
  }, [open]);

  // A new search should start at the top of the results, not wherever the
  // previous scroll happened to leave things.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-10 font-normal border-line hover:bg-brand-light/40"
        >
          {selected ? (
            <span className="flex items-center gap-2.5 min-w-0">
              <ItemCode code={selected.item_code} />
              <span className="truncate text-ink">{selected.name}</span>
            </span>
          ) : (
            <span className="text-muted2">{placeholder}</span>
          )}
          <ChevronsUpDown className="w-4 h-4 opacity-40 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2 border-b border-line">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
            <Input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code, name or category"
              className="pl-8 h-9 border-line"
            />
          </div>
        </div>
        {/*
          When this picker opens inside a dialog, the dialog's scroll lock
          swallows wheel events aimed at this list (it renders outside the
          dialog in the DOM). Applying the delta ourselves restores normal
          mouse-wheel scrolling.
        */}
        <div
          ref={listRef}
          onWheel={(e) => {
            const el = e.currentTarget;
            const canScroll = el.scrollHeight > el.clientHeight;
            if (!canScroll) return;
            el.scrollTop += e.deltaY;
            e.stopPropagation();
          }}
          className="max-h-[280px] overflow-y-auto overscroll-contain py-1"
        >
          {results.length === 0 && (
            <div className="px-3 py-6 text-center text-[13px] text-muted2">
              Nothing matches “{query}”.
            </div>
          )}
          {results.map((i) => {
            const empty = i.current_stock <= 0;
            return (
              <button
                key={i.item_code}
                type="button"
                onClick={() => {
                  onChange(i.item_code, i);
                  setOpen(false);
                }}
                className={cn(
                  "w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-brand-light/60 transition-colors",
                  value === i.item_code && "bg-brand-light"
                )}
              >
                <Check
                  className={cn(
                    "w-4 h-4 text-brand shrink-0",
                    value === i.item_code ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="min-w-0 flex-1">
                  <ItemCode code={i.item_code} />
                  <div className="text-[13px] text-ink truncate">{i.name}</div>
                </div>
                <div
                  className={cn(
                    "text-[12px] tnum shrink-0",
                    empty ? "text-stock-out font-medium" : "text-muted2"
                  )}
                >
                  {empty ? "none left" : `${i.current_stock} left`}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
